/**
 * useFavourites — shared hook for the "heart / bookmark" flow.
 *
 * Anonymous visitors accumulate favourites in localStorage. When they sign
 * in (or refresh with a valid session), the client sends the local list to
 * the backend once for a merge, then falls back to the server as the source
 * of truth.
 *
 * Keeps the API tiny (`isFavourite`, `toggle`, `list`) so `PropertyCard` and
 * `MyFavouritesPage` can share behaviour without prop-drilling.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { getApiUrl } from "@/utils/api";
import { useShopperAuth } from "@/context/ShopperAuthContext";

const API = getApiUrl();
const STORAGE_KEY = "slh.favourites.v1";

// --- localStorage helpers ---------------------------------------------------
function readLocal() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}
function writeLocal(list) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    // Broadcast to other tabs and to any other listener inside this tab —
    // React's localStorage change events only fire cross-tab, so we
    // supplement with a synthetic event on `window`.
    window.dispatchEvent(new CustomEvent("slh:favourites-changed"));
  } catch { /* quota — ignore */ }
}

export function useFavourites() {
  const { shopper } = useShopperAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncedForShopper, setSyncedForShopper] = useState(null);

  // Read the server list (source of truth when signed in).
  const refresh = useCallback(async () => {
    if (!shopper) {
      // Anonymous: read from localStorage.
      const local = readLocal();
      // Local entries are `{property_id, created_at, property?}` — keep as-is.
      setItems(local);
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/shopper/me/favorites`, {
        withCredentials: true,
      });
      setItems(data.favorites || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [shopper]);

  // On sign-in, one-time sync of any localStorage entries into the account.
  useEffect(() => {
    if (!shopper) {
      setSyncedForShopper(null);
      return;
    }
    if (syncedForShopper === shopper.shopper_id) return;
    const local = readLocal();
    const ids = local.map((x) => x.property_id).filter(Boolean);
    const doSync = async () => {
      if (ids.length > 0) {
        try {
          await axios.post(
            `${API}/shopper/me/favorites/sync`,
            { property_ids: ids },
            { withCredentials: true },
          );
          // Clear localStorage — server is now the source of truth.
          window.localStorage.removeItem(STORAGE_KEY);
        } catch { /* keep local if server unavailable */ }
      }
      setSyncedForShopper(shopper.shopper_id);
      await refresh();
    };
    doSync();
  }, [shopper, syncedForShopper, refresh]);

  // Initial load + listen for cross-instance updates.
  useEffect(() => {
    refresh();
    const onChange = () => refresh();
    window.addEventListener("slh:favourites-changed", onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener("slh:favourites-changed", onChange);
      window.removeEventListener("storage", onChange);
    };
  }, [refresh]);

  const idSet = new Set(items.map((f) => f.property_id).filter(Boolean));
  const isFavourite = (propertyId) => idSet.has(propertyId);

  const toggle = useCallback(async (property, opts = {}) => {
    const propertyId = typeof property === "string" ? property : property?.property_id;
    if (!propertyId) return { favorited: false };
    const currentlyFav = idSet.has(propertyId);

    if (shopper) {
      // Signed in → hit the server.
      try {
        if (currentlyFav) {
          await axios.delete(
            `${API}/shopper/me/favorites/${propertyId}`,
            { withCredentials: true },
          );
        } else {
          await axios.post(
            `${API}/shopper/me/favorites`,
            { property_id: propertyId },
            { withCredentials: true },
          );
        }
        await refresh();
        return { favorited: !currentlyFav };
      } catch {
        return { favorited: currentlyFav, error: true };
      }
    }
    // Anonymous → mutate localStorage.
    const local = readLocal();
    let next;
    if (currentlyFav) {
      next = local.filter((x) => x.property_id !== propertyId);
    } else {
      next = [
        {
          property_id: propertyId,
          created_at: new Date().toISOString(),
          property: opts.snapshot || (typeof property === "object" ? {
            property_id: propertyId,
            title: property?.title,
            price: property?.price,
            currency: property?.currency,
            display_address: property?.display_address,
            beds: property?.beds,
            baths: property?.baths,
            gallery: (property?.gallery || []).slice(0, 1),
          } : null),
        },
        ...local,
      ];
    }
    writeLocal(next);
    setItems(next);
    return { favorited: !currentlyFav };
  }, [shopper, idSet, refresh]);

  return { items, loading, isFavourite, toggle, refresh };
}
